import React from 'react';
import { useIntl } from 'react-intl';
import { Dialog, DialogTitle, IconButton, Button } from '@mui/material';
import styled from '@emotion/styled';
import { image } from 'src/theme';
import { DangerouslyHtml } from '@/components/item';
import { LoadingSpinner } from '@/components/loading';
import { useGetBasicPage } from '@/hooks/CommonRequests';
import { mq } from '@/utils/mediaQuery';
import { SignupStyledDialogContent } from './inquryStyles';

const CustomCheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M8 12L11 15L16 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TermsModalDialog = styled(Dialog)(() => ({
  overflowX: 'hidden',
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: '800px',
    marginLeft: 0,
    marginRight: 0,
    borderRadius: '12px',
  },
}));

const ModalHeader = styled(DialogTitle)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  fontWeight: 'bold',
  fontSize: '1.8rem',
}));

const TermsModalContent = styled(SignupStyledDialogContent)(() => ({
  position: 'relative',
  maxHeight: '70vh',
  overflowY: 'auto',

  '& ul, & li, & ol': {
    listStyle: 'revert',
  },
}));

export const TermsOverlay = styled.div(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
}));

const AgreeButtonWrapper = styled.div(() => ({
  padding: '24px',
  display: 'flex',
  justifyContent: 'center',
}));

const AgreeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.color.primary_color,
  color: '#fff',
  width: '100%',
  height: '4.2rem',
  padding: '16px',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.color.primary_color,
  },

  [mq('tablet')]: {
    width: '100%',
    height: '100%',
    fontSize: '1.8rem',
  },
}));

const InquiryTermsModal = ({ isOpen, onClose, alias, onAgree }) => {
  const intl = useIntl();

  const { data: getBasicPageInformation, isFetching } = useGetBasicPage(alias, {
    enabled: isOpen && !!alias,
  });

  return (
    <TermsModalDialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableScrollLock
    >
      <ModalHeader>
        {getBasicPageInformation?.title || '\u200b'}
        <IconButton onClick={onClose} size="large" aria-label="close">
          <img src={image.close.default} alt="close icon" />
        </IconButton>
      </ModalHeader>

      <TermsModalContent dividers>
        {isFetching ? (
          <TermsOverlay>
            <LoadingSpinner />
          </TermsOverlay>
        ) : (
          <DangerouslyHtml
            value={
              getBasicPageInformation?.field_content_bottom || getBasicPageInformation?.body || ''
            }
          />
        )}
      </TermsModalContent>

      {!isFetching && (
        <AgreeButtonWrapper>
          <AgreeButton onClick={onAgree} startIcon={<CustomCheckIcon />}>
            {intl.formatMessage({ id: 'lang-agree' })}
          </AgreeButton>
        </AgreeButtonWrapper>
      )}
    </TermsModalDialog>
  );
};

export default InquiryTermsModal;
